/**
 * @author        woody@zumper.com (Woody Anderson), yewon@zumper.com(Yewon Lee)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Bundle Singleton Manager
 *                - Usage examples
 *                  Api.bundle.begin() -> promise
 *                  Api.bundle.then(other) -> short form promise
 *                  Api.bundle.isLoggedIn() -> synchronous check of current state, bool
 *                  Api.bundle.listenForUserChange(callback) -> nothin.
 */

import { Bundle } from '@zcode/bizos'
import { definitelyError, localStorageSingleton, removeUserFromLocalStorage } from '@zcode/nr-utils'
import { logger } from '../../logger'
import ApiFetcher from './fetcher.singleton'
import * as TokenManager from './token_manager'
import zrsrc from './zrsrc'

/**
 * inner resource that simply talks to the api w/o caching or promise singletons etc.
 *
 * @type {{
 *   begin(*): Promise<Bundle>,
 * }}
 */
const _bundle = zrsrc(ApiFetcher, '/api/t/1/bundle', { begin: { method: 'GET' } }, {}, Bundle)

// using function notation to preserve lexical scoping
/**
 *
 * @param {typeof bundle} bundleRef
 * @param {Bundle} bizo
 * @param {typeof bundle['lastLogin']} [previousLogin]
 * @returns
 */
const bundleSuccessHelper = (bundleRef, bizo, previousLogin) => {
  if (bizo.user) {
    const good = previousLogin && previousLogin.userId === bizo.user.userId
    const timestamp = good ? previousLogin.timestamp : Date.now()
    const lastLogin = { timestamp, userId: bizo.user.userId, userEmail: bizo.user.email }

    localStorageSingleton.set('lastLogin', lastLogin)
    bundleRef.lastLogin = lastLogin

    // removing local user info from localStorage as we now have a logged in user and we don't want
    // to localStorage version of user to show before the bundle call returns and rerenders
    // components such as our message forms
    removeUserFromLocalStorage()
  } else {
    localStorageSingleton.del('lastLogin')
    bundleRef.lastLogin = null
  }
  TokenManager.set({ xzToken: bizo.xzToken, csrfToken: bizo.csrf })
  return bizo
}

// singleton that holds the bundle/user object & promise for the browser/tab session
const bundle = {
  /**
   * bundle object
   *
   * @type {null | Bundle}
   */
  value: null,
  /**
   * promise for the bundle object
   *
   * @type {Promise<Bundle>}
   */
  promise: null,
  /**
   * the last time we might have logged in according to the last time we checked.
   * if this != localStorageSingleton.get('lastLogin'), then someone's done something in another tab.
   * tell the registered callbacks about this insanity and probably nuke our 'value/promise' ?
   *
   * @type {null | { timestamp: number, userId: number, userEmail: string }}
   */
  lastLogin: null,
  // clears all the bundle values
  clear: () => {
    bundle.value = null
    bundle.promise = null
    bundle.lastLogin = null
  },
  /**
   *
   * @param {{ loginHandler: () => void; logoutHandler: () => void }} param0
   */
  init: ({ loginHandler, logoutHandler }) => {
    // init expected to be called during 'hydrate'. load the bundle if we've logged in before
    if (typeof window !== 'undefined') {
      if (document.cookie.split(';').some((cookie) => cookie.trim().startsWith('loggedout='))) {
        bundle.clear()
        logoutHandler()
        document.cookie = 'loggedout=;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT'
      }
      window.addEventListener('storage', () => {
        const current = localStorageSingleton.get('lastLogin') || {}
        const previous = bundle.lastLogin || {}

        if (previous.timestamp !== current.timestamp || previous.userId !== current.userId) {
          bundle.lastLogin = current
          if (current.userId && previous.userId !== current.userId) {
            logger.debug('different user from other tab logged in')
            bundle.clear()
            setTimeout(loginHandler)
          } else if (previous.userId && !current.userId) {
            logger.debug('logout from other tab')
            // logout occured in another tab.. cleanup?
            bundle.clear()
            setTimeout(logoutHandler)
          } else if (!previous.userId && current.userId) {
            logger.debug('login from other tab')
            // login occured in anothr tab... we think we're anonymous.. but that changed.
            bundle.clear()
            setTimeout(loginHandler)
          } else {
            logger.debug('detected re-login of same user from other tab')
          }
        }
      })
    }

    // init does an immediate check of lastLogin state.
    bundle.lastLogin =
      localStorageSingleton.get('lastLogin') ?? localStorageSingleton.get('ProlastLogin')
    if (bundle.lastLogin) {
      bundle.then(loginHandler).catch((err) => {
        const msg = 'Failed to login with lastLogin value from LocalStorage'
        logger.error(msg, definitelyError(msg, err))
      })
    } else if (!bundle.promise) {
      bundle.begin().then(loginHandler)
    }
  },
  /**
   *
   * @type {Promise<import('@zcode/state-type').Bundle>['then']}
   */
  then: (ok, err, noCache = false) => {
    return bundle.begin(noCache).then(ok, err)
  },
  /**
   *
   * @param {boolean} [noCache]
   */
  begin: (noCache) => {
    if (noCache || !bundle.promise) {
      bundle.promise = _bundle
        .begin()
        .then((bizo) => {
          if (bizo.user && bundle.lastLogin && bizo.user.userId !== bundle.lastLogin.userId) {
            // not logged in as the user we thought...
            logger.warn(
              'we began the bundle and are logged in as unexpected user',
              bundle.lastLogin,
              bizo.user.userId
            )
            // call the 'checker' function immediately?
            // localStorageSingleton.set('lastLogin', { timestamp: Date.now(), userId: bizo.user.userId })
          }
          bundle.value = bizo
          // passing lastLogin will cause the handler to preserve the timestamp for us.
          return bundleSuccessHelper(bundle, bizo, bundle.lastLogin)
        })
        .catch((err) => {
          bundle.value = null
          bundle.promise = null
          return Promise.reject(err)
        })
    }
    return bundle.promise
  },
  refresh: () => {
    localStorageSingleton.del('lastLogin')
    bundle.clear()
    return bundle.begin()
  },
  isLoggedIn: () => !!(bundle.value && bundle.value.user),
  isVerified: () => {
    logger.debug('isVerified was called on the bundle. PM has no verification currently')
  },
}

export default bundle
